(() => {
  const pages = document.querySelectorAll('[data-mma-product-page]');

  if (!pages.length) return;

  const formatMoneyMarkup = (cents) => {
    const value = Number(cents || 0);
    const reais = Math.floor(value / 100);
    const centavos = String(value % 100).padStart(2, '0');

    return `
      <span class="mma-price-prefix">R$</span>
      <span class="mma-price-main">${reais}</span><span class="mma-price-cents">,${centavos}</span>
    `;
  };


  const initPdpMenu = (page) => {
    const drawer = page.querySelector('[data-guardion-menu]');
    const panel = drawer?.querySelector('.guardion-menu-drawer__panel');
    const openButton = page.querySelector('[data-guardion-menu-open]');
    const closeButtons = Array.from(
      page.querySelectorAll('[data-guardion-menu-close]')
    );
    const firstFocus = page.querySelector('[data-guardion-menu-first-focus]');

    if (!drawer || !panel || !openButton) return;
    if (drawer.dataset.ready === 'true') return;

    drawer.dataset.ready = 'true';

    let previousFocus = null;

    const getFocusable = () =>
      Array.from(
        panel.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );

    const openMenu = () => {
      previousFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('guardion-menu-is-open');

      window.requestAnimationFrame(() => {
        firstFocus?.focus();
      });
    };

    const closeMenu = ({ restoreFocus = true } = {}) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('guardion-menu-is-open');

      if (restoreFocus && previousFocus instanceof HTMLElement) {
        previousFocus.focus();
      }

      previousFocus = null;
    };

    const trapFocus = (event) => {
      if (event.key !== 'Tab' || !drawer.classList.contains('is-open')) return;

      const focusable = getFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
        event.preventDefault();
        closeMenu();
        return;
      }

      trapFocus(event);
    };

    openButton.addEventListener('click', openMenu);

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => closeMenu());
    });

    drawer.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => closeMenu({ restoreFocus: false }));
    });

    document.addEventListener('keydown', handleKeydown);

    drawer.mmaMenuClose = closeMenu;
    drawer.mmaMenuDestroy = () => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.classList.remove('guardion-menu-is-open');
    };
  };

  const initGallery = (page) => {
    const gallery = page.querySelector('[data-product-gallery]');

    if (!gallery || gallery.dataset.galleryReady === 'true') return;

    gallery.dataset.galleryReady = 'true';

    const track = gallery.querySelector('[data-gallery-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const dots = Array.from(gallery.querySelectorAll('[data-gallery-dot]'));
    const prevButton = gallery.querySelector('[data-gallery-prev]');
    const nextButton = gallery.querySelector('[data-gallery-next]');

    if (!track || !slides.length) return;

    let galleryTicking = false;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchCurrentX = 0;
    let touchCurrentY = 0;
    let isTouching = false;
    let isHorizontalGesture = false;

    const clampIndex = (index) => {
      return Math.max(0, Math.min(index, slides.length - 1));
    };

    const getActiveIndex = () => {
      if (!track.clientWidth) return 0;
      return clampIndex(Math.round(track.scrollLeft / track.clientWidth));
    };

    const setActiveDot = (activeIndex) => {
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;

        dot.classList.toggle('is-active', isActive);

        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    };

    const updateGalleryState = () => {
      setActiveDot(getActiveIndex());
      galleryTicking = false;
    };

    const goToSlide = (index) => {
      const nextIndex = clampIndex(index);
      const slide = slides[nextIndex];

      if (!slide) return;

      track.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth',
      });

      setActiveDot(nextIndex);
    };

    track.addEventListener(
      'scroll',
      () => {
        if (galleryTicking) return;

        galleryTicking = true;
        window.requestAnimationFrame(updateGalleryState);
      },
      { passive: true }
    );

    dots.forEach((dot) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        goToSlide(Number(dot.dataset.galleryIndex || 0));
      });
    });

    prevButton?.addEventListener('click', (event) => {
      event.preventDefault();
      goToSlide(getActiveIndex() - 1);
    });

    nextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      goToSlide(getActiveIndex() + 1);
    });

    track.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) {
          isTouching = false;
          isHorizontalGesture = false;
          return;
        }

        const touch = event.touches[0];

        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchCurrentX = touchStartX;
        touchCurrentY = touchStartY;
        isTouching = true;
        isHorizontalGesture = false;
      },
      { passive: true }
    );

    track.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length !== 1) {
          isTouching = false;
          isHorizontalGesture = false;
          return;
        }

        if (!isTouching) return;

        const touch = event.touches[0];

        touchCurrentX = touch.clientX;
        touchCurrentY = touch.clientY;

        const deltaX = touchCurrentX - touchStartX;
        const deltaY = touchCurrentY - touchStartY;

        if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY)) {
          isHorizontalGesture = true;
          event.preventDefault();
        }
      },
      { passive: false }
    );

    const finishTouch = () => {
      if (!isTouching) return;

      const deltaX = touchCurrentX - touchStartX;
      const shouldChangeSlide = isHorizontalGesture && Math.abs(deltaX) >= 45;

      isTouching = false;

      if (!shouldChangeSlide) return;

      goToSlide(getActiveIndex() + (deltaX < 0 ? 1 : -1));
    };

    track.addEventListener('touchend', finishTouch);
    track.addEventListener('touchcancel', finishTouch);
    window.addEventListener('resize', updateGalleryState);

    updateGalleryState();
  };

  const initGalleryLightbox = (page) => {
    const lightbox = page.querySelector('[data-gallery-lightbox]');
    const openButton = page.querySelector('[data-gallery-lightbox-open]');

    if (
      !lightbox ||
      !openButton ||
      lightbox.dataset.lightboxReady === 'true'
    ) {
      return;
    }

    const dialog = lightbox.querySelector('[data-gallery-lightbox-dialog]');
    const closeButtons = Array.from(
      lightbox.querySelectorAll('[data-gallery-lightbox-close]')
    );
    const slides = Array.from(
      lightbox.querySelectorAll('[data-lightbox-slide]')
    );
    const prevButton = lightbox.querySelector('[data-lightbox-prev]');
    const nextButton = lightbox.querySelector('[data-lightbox-next]');
    const currentElement = lightbox.querySelector('[data-lightbox-current]');
    const closeControl = dialog.querySelector('[data-gallery-lightbox-close]');

    if (!dialog || !slides.length) return;

    lightbox.dataset.lightboxReady = 'true';

    let currentIndex = 0;
    let previousFocus = null;

    const normalizeIndex = (index) => {
      if (index < 0) return slides.length - 1;
      if (index >= slides.length) return 0;
      return index;
    };

    const showSlide = (index) => {
      currentIndex = normalizeIndex(index);

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === currentIndex;

        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      if (currentElement) {
        currentElement.textContent = String(currentIndex + 1);
      }
    };

    const getFocusableElements = () => {
      return Array.from(
        dialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => {
        return element.offsetParent !== null;
      });
    };

    const closeLightbox = ({ restoreFocus = true } = {}) => {
      if (lightbox.hidden) return;

      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      lightbox.hidden = true;
      document.body.classList.remove('mma-gallery-modal-open');

      if (restoreFocus && previousFocus instanceof HTMLElement) {
        previousFocus.focus();
      }

      previousFocus = null;
    };

    const openLightbox = (index) => {
      previousFocus = document.activeElement;
      showSlide(index);

      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('mma-gallery-modal-open');

      window.requestAnimationFrame(() => {
        lightbox.classList.add('is-open');

        if (closeControl) {
          closeControl.focus();
        } else {
          dialog.focus();
        }
      });
    };

    const handleKeydown = (event) => {
      if (lightbox.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeLightbox();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showSlide(currentIndex - 1);
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showSlide(currentIndex + 1);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();

      if (!focusableElements.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        (document.activeElement === firstElement ||
          document.activeElement === dialog)
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === lastElement ||
          document.activeElement === dialog)
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    openButton.addEventListener('click', () => {
      const startIndex = Number(
        openButton.dataset.galleryLightboxIndex || 0
      );

      openLightbox(startIndex);
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        closeLightbox();
      });
    });

    prevButton?.addEventListener('click', () => {
      showSlide(currentIndex - 1);
    });

    nextButton?.addEventListener('click', () => {
      showSlide(currentIndex + 1);
    });

    document.addEventListener('keydown', handleKeydown);

    lightbox.mmaGalleryClose = closeLightbox;
    showSlide(0);
  };

  const initProductForm = (page) => {
    if (page.dataset.productFormReady === 'true') return;

    page.dataset.productFormReady = 'true';

    const form = page.querySelector('[data-product-form]');
    const productJsonElement = page.querySelector('[data-product-json]');
    const variantInput = page.querySelector('[data-variant-input]');
    const priceElement = page.querySelector('[data-product-price]');
    const comparePriceElement = page.querySelector('[data-product-compare-price]');
    const checkoutButton = page.querySelector('[data-direct-checkout]');
    const optionButtons = Array.from(page.querySelectorAll('[data-option-button]'));

    if (!form || !productJsonElement || !variantInput || !checkoutButton) return;

    let product;

    try {
      product = JSON.parse(productJsonElement.textContent);
    } catch (error) {
      console.error('Erro ao ler os dados do produto:', error);
      return;
    }

    const getSelectedOptions = () => {
      const selectedOptions = [];

      optionButtons.forEach((button) => {
        if (!button.classList.contains('is-selected')) return;

        selectedOptions[Number(button.dataset.optionIndex)] = button.dataset.optionValue;
      });

      return selectedOptions;
    };

    const findSelectedVariant = () => {
      if (!optionButtons.length) {
        return product.variants.find((variant) => String(variant.id) === String(variantInput.value))
          || product.variants[0];
      }

      const selectedOptions = getSelectedOptions();

      return product.variants.find((variant) => {
        return variant.options.every((optionValue, index) => {
          return optionValue === selectedOptions[index];
        });
      });
    };

    const updateUnavailableButtons = () => {
      if (!optionButtons.length) return;

      const selectedOptions = getSelectedOptions();

      optionButtons.forEach((button) => {
        const optionIndex = Number(button.dataset.optionIndex);
        const testOptions = [...selectedOptions];

        testOptions[optionIndex] = button.dataset.optionValue;

        const hasAvailableVariant = product.variants.some((variant) => {
          const matchesOptions = variant.options.every((value, index) => {
            return !testOptions[index] || value === testOptions[index];
          });

          return matchesOptions && variant.available;
        });

        button.disabled = !hasAvailableVariant;
        button.classList.toggle('is-disabled', !hasAvailableVariant);
        button.setAttribute('aria-disabled', String(!hasAvailableVariant));
      });
    };

    const updateProductState = () => {
      const variant = findSelectedVariant();

      if (!variant) {
        checkoutButton.disabled = true;
        checkoutButton.textContent = 'Escolha o tamanho';
        return;
      }

      variantInput.value = variant.id;

      if (priceElement) {
        priceElement.innerHTML = formatMoneyMarkup(variant.price);
      }

      if (comparePriceElement) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceElement.innerHTML = formatMoneyMarkup(variant.compare_at_price);
          comparePriceElement.classList.remove('is-hidden');
        } else {
          comparePriceElement.innerHTML = '';
          comparePriceElement.classList.add('is-hidden');
        }
      }

      if (variant.available) {
        checkoutButton.disabled = false;
        checkoutButton.textContent = 'Comprar agora';
      } else {
        checkoutButton.disabled = true;
        checkoutButton.textContent = 'Esgotado';
      }

      updateUnavailableButtons();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;

        const optionIndex = button.dataset.optionIndex;

        optionButtons
          .filter((item) => item.dataset.optionIndex === optionIndex)
          .forEach((item) => {
            item.classList.remove('is-selected');
            item.setAttribute('aria-pressed', 'false');
          });

        button.classList.add('is-selected');
        button.setAttribute('aria-pressed', 'true');

        updateProductState();
      });
    });

    checkoutButton.addEventListener('click', async () => {
      const variantId = Number(variantInput.value);

      if (!variantId || checkoutButton.disabled) return;

      const root = window.Shopify?.routes?.root || '/';
      const originalLabel = checkoutButton.textContent;

      checkoutButton.disabled = true;
      checkoutButton.textContent = 'Processando...';

      try {
        const clearResponse = await fetch(`${root}cart/clear.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!clearResponse.ok) {
          throw new Error('Não foi possível limpar o carrinho.');
        }

        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
              },
            ],
          }),
        });

        if (!addResponse.ok) {
          throw new Error('Não foi possível adicionar a variante ao carrinho.');
        }

        window.location.assign(`${root}checkout`);
      } catch (error) {
        console.error('Erro ao iniciar o checkout:', error);
        checkoutButton.disabled = false;
        checkoutButton.textContent = originalLabel || 'Tentar novamente';
      }
    });

    updateProductState();
  };

  const initReviewCarousel = (carousel) => {
    if (!carousel || carousel.dataset.reviewCarouselReady === 'true') return;

    const reviewPages = Array.from(
      carousel.querySelectorAll('[data-review-page]')
    );

    if (!reviewPages.length) return;

    carousel.dataset.reviewCarouselReady = 'true';

    const viewport = carousel.querySelector('[data-review-viewport]');
    const prevButton = carousel.querySelector('[data-review-prev]');
    const nextButton = carousel.querySelector('[data-review-next]');
    const currentElement = carousel.querySelector('[data-review-current]');
    const totalElement = carousel.querySelector('[data-review-total]');

    let currentIndex = Math.max(
      0,
      reviewPages.findIndex((reviewPage) =>
        reviewPage.classList.contains('is-active')
      )
    );
    let touchStartX = 0;
    let touchStartY = 0;
    let touchCurrentX = 0;
    let touchCurrentY = 0;
    let isTouching = false;

    const clampIndex = (index) => {
      return Math.max(0, Math.min(index, reviewPages.length - 1));
    };

    const updateControls = () => {
      if (currentElement) {
        currentElement.textContent = String(currentIndex + 1);
      }

      if (totalElement) {
        totalElement.textContent = String(reviewPages.length);
      }

      if (prevButton) {
        prevButton.disabled = currentIndex === 0;
      }

      if (nextButton) {
        nextButton.disabled = currentIndex === reviewPages.length - 1;
      }
    };

    const showPage = (index, direction = 'next') => {
      const nextIndex = clampIndex(index);

      reviewPages.forEach((reviewPage, pageIndex) => {
        const isActive = pageIndex === nextIndex;

        reviewPage.classList.remove(
          'is-active',
          'is-entering-next',
          'is-entering-prev'
        );
        reviewPage.setAttribute('aria-hidden', String(!isActive));

        if (!isActive) return;

        reviewPage.classList.add('is-active');

        if (nextIndex !== currentIndex) {
          reviewPage.classList.add(
            direction === 'prev' ? 'is-entering-prev' : 'is-entering-next'
          );

          window.setTimeout(() => {
            reviewPage.classList.remove(
              'is-entering-next',
              'is-entering-prev'
            );
          }, 260);
        }
      });

      currentIndex = nextIndex;
      updateControls();
    };

    carousel.mmaReviewGoTo = (index) => {
      const direction = index < currentIndex ? 'prev' : 'next';
      showPage(index, direction);
    };

    prevButton?.addEventListener('click', () => {
      showPage(currentIndex - 1, 'prev');
    });

    nextButton?.addEventListener('click', () => {
      showPage(currentIndex + 1, 'next');
    });

    viewport?.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) {
          isTouching = false;
          return;
        }

        const touch = event.touches[0];

        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchCurrentX = touchStartX;
        touchCurrentY = touchStartY;
        isTouching = true;
      },
      { passive: true }
    );

    viewport?.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length !== 1) {
          isTouching = false;
          return;
        }

        if (!isTouching) return;

        const touch = event.touches[0];

        touchCurrentX = touch.clientX;
        touchCurrentY = touch.clientY;
      },
      { passive: true }
    );

    const finishTouch = () => {
      if (!isTouching) return;

      const deltaX = touchCurrentX - touchStartX;
      const deltaY = touchCurrentY - touchStartY;
      const isHorizontalSwipe =
        Math.abs(deltaX) >= 48 &&
        Math.abs(deltaX) > Math.abs(deltaY);

      isTouching = false;

      if (!isHorizontalSwipe) return;

      if (deltaX < 0) {
        showPage(currentIndex + 1, 'next');
      } else {
        showPage(currentIndex - 1, 'prev');
      }
    };

    viewport?.addEventListener('touchend', finishTouch);
    viewport?.addEventListener('touchcancel', finishTouch);

    showPage(currentIndex);
  };

  const initAllReviewCarousels = (scope = document) => {
    const carousels = [];

    if (scope.matches?.('[data-review-carousel]')) {
      carousels.push(scope);
    }

    scope
      .querySelectorAll?.('[data-review-carousel]')
      .forEach((carousel) => carousels.push(carousel));

    carousels.forEach(initReviewCarousel);
  };

  const initPage = (page) => {
    initPdpMenu(page);
    initGallery(page);
    initGalleryLightbox(page);
    initProductForm(page);
  };

  pages.forEach(initPage);

  initAllReviewCarousels();

  document.addEventListener('shopify:section:load', (event) => {
    const page = event.target.querySelector?.('[data-mma-product-page]');

    if (page) {
      initPage(page);
    }

    initAllReviewCarousels(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target
      .querySelectorAll?.('[data-gallery-lightbox]')
      .forEach((lightbox) => {
        lightbox.mmaGalleryClose?.({ restoreFocus: false });
      });

    event.target
      .querySelectorAll?.('[data-guardion-menu]')
      .forEach((drawer) => {
        drawer.mmaMenuClose?.({ restoreFocus: false });
        drawer.mmaMenuDestroy?.();
      });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const reviewCard = event.target.closest?.('[data-review-block-id]');

    if (!reviewCard) return;

    const reviewPage = reviewCard.closest('[data-review-page]');
    const carousel = reviewCard.closest('[data-review-carousel]');
    const pageIndex = Number(reviewPage?.dataset.reviewPageIndex || 0);

    carousel?.mmaReviewGoTo?.(pageIndex);
  });
})();
